import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Shield, Heart, Users, Award, Mail, MapPin, PhoneCall, Phone } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    birthDate: '',
    streetAddress: '',
    city: '',
    state: 'CO',
    zipCode: '',
    phoneNumber: '',
    email: '',
    coverages: [],
    preferredContact: [],
    tcpaConsent: false,
    textMessagingOptIn: false,
    creditConsent: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [showCallbackModal, setShowCallbackModal] = useState(false);
  const [callbackPhone, setCallbackPhone] = useState('');
  const [callbackName, setCallbackName] = useState('');
  const [isRequestingCallback, setIsRequestingCallback] = useState(false);

  // Setup Turnstile callback
  useEffect(() => {
    window.onTurnstileSuccess = (token) => {
      setTurnstileToken(token);
    };

    return () => {
      delete window.onTurnstileSuccess;
    };
  }, []);

  // Load EmbedSocial reviews widget
  useEffect(() => {
    if (document.getElementById('EmbedSocialHashtagScript')) return;
    const js = document.createElement('script');
    js.id = 'EmbedSocialHashtagScript';
    js.src = 'https://embedsocial.com/cdn/ht.js';
    document.head.appendChild(js);
  }, []);

  const handleRequestCallback = async () => {
    if (!callbackPhone) {
      toast.error('Please enter your phone number');
      return;
    }
    
    setIsRequestingCallback(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/request-callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: callbackPhone,
          name: callbackName
        })
      });
      
      const result = await response.json();
      
      if (result.status === 'success') {
        toast.success('Connecting you now! Please stay on the line.');
        setShowCallbackModal(false);
        setCallbackPhone('');
        setCallbackName('');
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error('Callback error:', error);
      toast.error('Unable to connect. Please call us directly at (970) 825-0442');
    } finally {
      setIsRequestingCallback(false);
    }
  };

  // Check if current time is within office hours (Mountain Time)
  const isOfficeHours = () => {
    // Get current time in Mountain Time
    const now = new Date();
    const options = { timeZone: 'America/Denver', hour: 'numeric', minute: 'numeric', hour12: false };
    const mtTime = new Intl.DateTimeFormat('en-US', options).format(now);
    const [hour] = mtTime.split(':').map(Number);
    
    // Get day of week (0 = Sunday, 6 = Saturday)
    const dayOptions = { timeZone: 'America/Denver', weekday: 'short' };
    const dayOfWeek = new Intl.DateTimeFormat('en-US', dayOptions).format(now);
    
    // Office closed on weekends
    if (dayOfWeek === 'Sat' || dayOfWeek === 'Sun') {
      return false;
    }
    
    // Friday: 9 AM - 4 PM
    if (dayOfWeek === 'Fri') {
      return hour >= 9 && hour < 16;
    }
    
    // Monday-Thursday: 9 AM - 5 PM
    return hour >= 9 && hour < 17;
  };

  const coverageOptions = [
  'Home',
  'Auto',
  'Specialty',
  'Umbrella',
  'Life',
  'Annuities',
  'Business/Commercial',
  'Other'];


  const handleCoverageChange = (coverage) => {
    setFormData((prev) => ({
      ...prev,
      coverages: prev.coverages.includes(coverage) ?
      prev.coverages.filter((c) => c !== coverage) :
      [...prev.coverages, coverage]
    }));
  };

  const handlePreferredContactChange = (method) => {
    setFormData((prev) => ({
      ...prev,
      preferredContact: prev.preferredContact.includes(method) ?
      prev.preferredContact.filter((m) => m !== method) :
      [...prev.preferredContact, method]
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.tcpaConsent) {
      toast.error('Please provide TCPA/PEWC consent to continue');
      return;
    }
    if (!formData.creditConsent) {
      toast.error('Please authorize insurance score inquiry to continue');
      return;
    }
    if (formData.coverages.length === 0) {
      toast.error('Please select at least one coverage type');
      return;
    }

    setIsSubmitting(true);

    try {
      // Generate unique submission ID
      const submissionId = `sub_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Add submission ID to form data - always use CO for state
      const submissionData = {
        ...formData,
        state: 'CO',  // Always Colorado
        submission_id: submissionId,
        turnstile_token: turnstileToken || 'not_required'
      };

      // Store form data in localStorage for Advanced Quote page
      localStorage.setItem(`formData_${submissionId}`, JSON.stringify(formData));

      // Submit lead directly to our backend (which integrates with AgencyZoom)
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/submit-lead`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      const result = await response.json();
      
      if (result.status === 'success') {
        // Store submission ID for thank you page to fetch agent info
        localStorage.setItem('submissionId', result.submission_id || submissionId);
        window.location.href = '/thank-you';
      } else {
        throw new Error(result.message || 'Failed to submit lead');
      }
      
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Unable to submit form. Please try again or contact us directly at (970) 315-4628');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Callback Modal */}
      <Dialog open={showCallbackModal} onOpenChange={setShowCallbackModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#003087]">Request a Callback</DialogTitle>
            <DialogDescription>
              Enter your phone number and we'll connect you with an agent right away.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label htmlFor="callback-name">Your Name (Optional)</Label>
              <Input
                id="callback-name"
                value={callbackName}
                onChange={(e) => setCallbackName(e.target.value)}
                placeholder="John Doe"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="callback-phone">Phone Number *</Label>
              <Input
                id="callback-phone"
                type="tel"
                value={callbackPhone}
                onChange={(e) => setCallbackPhone(e.target.value)}
                placeholder="(970) 555-1234"
                className="mt-1"
                required
              />
            </div>
            <Button
              onClick={handleRequestCallback}
              disabled={isRequestingCallback}
              className="w-full bg-[#e01933] hover:bg-[#b30032] text-white"
            >
              {isRequestingCallback ? 'Connecting...' : 'Call Me Now'}
            </Button>
            <p className="text-xs text-gray-500 text-center">
              Or call us directly: <a href="tel:+19708250442" className="text-[#003087] font-medium">(970) 825-0442</a>
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <img
                src="https://customer-assets.emergentagent.com/job_apagency-farmers/artifacts/m4aqjodf_image.jpg"
                alt="AP Agency Logo"
                className="h-12 w-auto" />

              <div>
                <p className="text-xs text-gray-600">Representing Farmers Insurance</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {isOfficeHours() ? (
                <Button
                  onClick={() => setShowCallbackModal(true)}
                  className="bg-[#e01933] hover:bg-[#b30032] text-white transition-all duration-300"
                >
                  <PhoneCall className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">Call Me</span>
                  <span className="sm:hidden">Call</span>
                </Button>
              ) : (
                <span className="text-sm text-gray-600 hidden sm:block">
                  Office Hours: Mon-Thu 9-5, Fri 9-4
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#003087] via-[#003087] to-[#b4c4ea] text-white py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1588979355313-6711a095465f"
            alt="Family protection"
            className="w-full h-full object-cover" />

        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Personalized Insurance Quotes from AP Agency
            </h2>
            <p className="text-xl md:text-2xl mb-4 text-[#b4c4ea]">
              In a world of call centers and AI, connect with a real local agent who knows your community
            </p>
            <p className="text-lg md:text-xl mb-10 text-white/90">
              Our licensed team at AP Agency specializes in tailoring the right coverages to fit your unique needs
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                onClick={scrollToForm}
                className="bg-[#e01933] hover:bg-[#b30032] text-white text-lg px-8 py-6 transition-all duration-300 hover:shadow-lg hover:scale-105">

                Get My Free Quote Now
              </Button>
              {isOfficeHours() && (
                <Button
                  size="lg"
                  onClick={() => setShowCallbackModal(true)}
                  variant="outline"
                  className="bg-white text-[#003087] border-2 border-white hover:bg-[#b4c4ea] hover:text-[#003087] text-lg px-8 py-6 transition-all duration-300 hover:shadow-lg">

                  <PhoneCall className="h-5 w-5 mr-2" />
                  Talk to an Agent Now
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Lead Capture Form Section */}
      <section id="quote-form" className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-xl p-8 md:p-12">
            <div className="text-center mb-10">
              <h3 className="text-3xl md:text-4xl font-bold text-[#003087] mb-4">
                Get Your Free Personalized Quote
              </h3>
              <p className="text-lg text-gray-600">
                Fill out the form below and one of our licensed agents will contact you with your customized quote
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="firstName" className="text-[#003087] font-semibold">First Name *</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                    className="mt-2 border-gray-300 focus:border-[#003087] focus:ring-[#003087]" />

                </div>
                <div>
                  <Label htmlFor="lastName" className="text-[#003087] font-semibold">Last Name *</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                    className="mt-2 border-gray-300 focus:border-[#003087] focus:ring-[#003087]" />

                </div>
              </div>

              {/* Birth Date */}
              <div>
                <Label htmlFor="birthDate" className="text-[#003087] font-semibold">Birth Date *</Label>
                <Input
                  id="birthDate"
                  name="birthDate"
                  type="date"
                  required
                  value={formData.birthDate}
                  onChange={handleChange}
                  className="mt-2 border-gray-300 focus:border-[#003087] focus:ring-[#003087]" />

              </div>

              {/* Address Fields */}
              <div>
                <Label htmlFor="streetAddress" className="text-[#003087] font-semibold">Street Address *</Label>
                <Input
                  id="streetAddress"
                  name="streetAddress"
                  type="text"
                  required
                  value={formData.streetAddress}
                  onChange={handleChange}
                  className="mt-2 border-gray-300 focus:border-[#003087] focus:ring-[#003087]" />

              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label htmlFor="city" className="text-[#003087] font-semibold">City *</Label>
                  <Input
                    id="city"
                    name="city"
                    type="text"
                    required
                    value={formData.city}
                    onChange={handleChange}
                    className="mt-2 border-gray-300 focus:border-[#003087] focus:ring-[#003087]" />

                </div>
                <div>
                  <Label htmlFor="state" className="text-[#003087] font-semibold">State *</Label>
                  <Input
                    id="state"
                    name="state"
                    type="text"
                    required
                    value="Colorado"
                    readOnly
                    onClick={() => toast.info('Our Agency is only licensed in Colorado')}
                    className="mt-2 border-gray-300 bg-gray-100 cursor-not-allowed" />

                </div>
                <div>
                  <Label htmlFor="zipCode" className="text-[#003087] font-semibold">Zip Code *</Label>
                  <Input
                    id="zipCode"
                    name="zipCode"
                    type="text"
                    required
                    pattern="[0-9]{5}"
                    value={formData.zipCode}
                    onChange={handleChange}
                    className="mt-2 border-gray-300 focus:border-[#003087] focus:ring-[#003087]" />

                </div>
              </div>

              {/* Contact Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="phoneNumber" className="text-[#003087] font-semibold">Phone Number *</Label>
                  <Input
                    id="phoneNumber"
                    name="phoneNumber"
                    type="tel"
                    required
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    className="mt-2 border-gray-300 focus:border-[#003087] focus:ring-[#003087]" />

                </div>
                <div>
                  <Label htmlFor="email" className="text-[#003087] font-semibold">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="mt-2 border-gray-300 focus:border-[#003087] focus:ring-[#003087]" />

                </div>
              </div>

              {/* Preferred Contact Method */}
              <div>
                <Label className="text-[#003087] font-semibold mb-3 block">Preferred Contact Method</Label>
                <div className="flex flex-wrap gap-6">
                  {['Phone', 'Text', 'Email'].map((method) =>
                    <div key={method} className="flex items-center space-x-2">
                      <Checkbox
                        id={`contact-${method}`}
                        checked={formData.preferredContact.includes(method)}
                        onCheckedChange={() => handlePreferredContactChange(method)}
                        className="border-[#003087] data-[state=checked]:bg-[#003087]"
                        data-testid={`preferred-contact-${method.toLowerCase()}`}
                      />
                      <label
                        htmlFor={`contact-${method}`}
                        className="text-sm font-medium text-gray-700 cursor-pointer"
                      >
                        {method}
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* Coverage Multi-select */}
              <div>
                <Label className="text-[#003087] font-semibold mb-3 block">Select Coverage Types *</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {coverageOptions.map((coverage) =>
                  <div key={coverage} className="flex items-center space-x-2">
                      <Checkbox
                      id={coverage}
                      checked={formData.coverages.includes(coverage)}
                      onCheckedChange={() => handleCoverageChange(coverage)}
                      className="border-[#003087] data-[state=checked]:bg-[#003087]" />

                      <label
                      htmlFor={coverage}
                      className="text-sm font-medium text-gray-700 cursor-pointer">

                        {coverage}
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* Consent Checkboxes */}
              <div className="space-y-4 bg-gray-50 p-6 rounded-lg border border-gray-200">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="tcpaConsent"
                    checked={formData.tcpaConsent}
                    onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, tcpaConsent: checked }))}
                    className="mt-1 border-[#003087] data-[state=checked]:bg-[#003087]" />

                  <label htmlFor="tcpaConsent" className="text-sm text-gray-700 cursor-pointer">
                    <span className="font-semibold text-[#e01933]">*Required:</span> I provide my prior express written consent to receive calls and emails from AP Agency (Farmers Insurance) at the phone number and email provided, even if on the Do Not Call list, or if previously unsubscribed. Consent is not a condition of purchase.
                  </label>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="textMessagingOptIn"
                    checked={formData.textMessagingOptIn}
                    onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, textMessagingOptIn: checked }))}
                    className="mt-1 border-[#003087] data-[state=checked]:bg-[#003087]" />

                  <label htmlFor="textMessagingOptIn" className="text-sm text-gray-700 cursor-pointer">
                    <span className="font-semibold text-[#003087]">Optional:</span> Yes, I would like to Opt-in to receive text messages from AP Agency. I understand I can opt out at any time by replying STOP.
                  </label>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="creditConsent"
                    checked={formData.creditConsent}
                    onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, creditConsent: checked }))}
                    className="mt-1 border-[#003087] data-[state=checked]:bg-[#003087]" />

                  <label htmlFor="creditConsent" className="text-sm text-gray-700 cursor-pointer">
                    <span className="font-semibold text-[#e01933]">*Required:</span> I authorize a soft credit inquiry (insurance score) to help provide accurate quotes. This will not affect my credit score.
                  </label>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-xs text-gray-600 leading-relaxed">
                  <strong>Disclaimer:</strong> Quotes are non-binding. Your information will be shared with a licensed Farmers Insurance agent at AP Agency for personalized service. Licensed in Colorado.
                </p>
              </div>

              {/* Invisible Turnstile Widget */}
              <div className="cf-turnstile" 
                   data-sitekey="0x4AAAAAACatQLH0Ol6CBBzO"
                   data-callback="onTurnstileSuccess"
                   data-theme="light"
                   data-appearance="interaction-only">
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="w-full bg-[#e01933] hover:bg-[#b30032] text-white text-lg py-6 transition-all duration-300 hover:shadow-lg disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Submit & Get My Quote'}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <img
                src="https://www.farmers.com/contentdam/farmers/articles/auto/2017-auto/farmers-insurance-logo-tagline.svg"
                alt="Farmers Insurance"
                className="h-16"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }} />

              <div style={{ display: 'none' }} className="text-2xl font-bold text-[#003087]">
                Farmers Insurance
              </div>
            </div>
            <h3 className="text-3xl font-bold text-[#003087] mb-4">
              Licensed Colorado Agency | Representing Farmers Insurance
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <div className="text-center p-6 bg-gray-50 rounded-lg hover:shadow-lg transition-shadow duration-300">
              <div className="flex justify-center mb-4">
                <Shield className="h-12 w-12 text-[#003087]" />
              </div>
              <h4 className="text-lg font-bold text-[#003087] mb-2">Personalized Coverage</h4>
              <p className="text-gray-600">Tailored insurance solutions that fit your unique needs and budget</p>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-lg hover:shadow-lg transition-shadow duration-300">
              <div className="flex justify-center mb-4">
                <Users className="h-12 w-12 text-[#003087]" />
              </div>
              <h4 className="text-lg font-bold text-[#003087] mb-2">Local Expertise</h4>
              <p className="text-gray-600">Real agents who know Loveland and Colorado insurance requirements</p>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-lg hover:shadow-lg transition-shadow duration-300">
              <div className="flex justify-center mb-4">
                <Heart className="h-12 w-12 text-[#003087]" />
              </div>
              <h4 className="text-lg font-bold text-[#003087] mb-2">Community Care</h4>
              <p className="text-gray-600">We're your neighbors, committed to protecting what matters most</p>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-lg hover:shadow-lg transition-shadow duration-300">
              <div className="flex justify-center mb-4">
                <Award className="h-12 w-12 text-[#003087]" />
              </div>
              <h4 className="text-lg font-bold text-[#003087] mb-2">Trusted Service</h4>
              <p className="text-gray-600">Backed by Farmers Insurance's 90+ years of experience</p>
            </div>
          </div>

          {/* Google Reviews Section - EmbedSocial */}
          <div className="bg-gradient-to-br from-gray-50 to-[#b4c4ea]/10 rounded-lg p-8" data-testid="reviews-section">
            <h4 className="text-2xl font-bold text-[#003087] mb-6 text-center">What Our Clients Say</h4>
            <div className="embedsocial-hashtag" data-ref="9299b5ebf400056a7405445e0166a168531f2c07"></div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#003087] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_apagency-farmers/artifacts/m4aqjodf_image.jpg"
                  alt="AP Agency Logo"
                  className="h-12 w-auto" />

                <div>
                  <p className="text-sm text-[#b4c4ea]">Representing Farmers Insurance</p>
                </div>
              </div>
              <p className="text-[#b4c4ea] text-sm">Your trusted local insurance partner in Loveland, Colorado. Licensed and dedicated to serving our community.
CO License: 852298
              </p>
            </div>

            <div>
              <h5 className="text-lg font-bold mb-4">Contact Us</h5>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <MapPin className="h-5 w-5 text-[#b4c4ea] mt-0.5 flex-shrink-0" />
                  <span className="text-sm">383 W 37th Ste Ste 202<br />Loveland, CO 80538</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="h-5 w-5 text-[#b4c4ea] flex-shrink-0" />
                  <a href="tel:+19703154628" className="text-sm hover:text-white transition-colors">(970) 315-4628</a>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-[#b4c4ea] flex-shrink-0" />
                  <a href="mailto:staff@apagency.us" className="text-sm hover:text-white transition-colors">staff@apagency.us</a>
                </div>
              </div>
            </div>

            <div>
              <h5 className="text-lg font-bold mb-4">Office Hours</h5>
              <ul className="space-y-2 text-sm text-[#b4c4ea]">
                <li>Monday - Thursday: 9:00 AM - 5:00 PM</li>
                <li>Friday: 9:00 AM - 4:00 PM</li>
                <li>Saturday - Sunday: Closed</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-[#b4c4ea]/30 pt-8 text-center">
            <p className="text-sm text-[#b4c4ea]">
              © {new Date().getFullYear()} AP Agency | Representing Farmers Insurance | Loveland, CO | Licensed in Colorado
            </p>
            <div className="mt-2">
              <a href="/privacy" className="text-xs text-[#b4c4ea] hover:text-white transition-colors">Privacy Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>);

};

export default Home;